import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import TenantLoginLink from '../components/TenantLoginLink.jsx';
import './SuperadminTenants.css';

const BRAND_PRESETS = {
    alinea: {
        label: 'ALINEA (dorado)',
        primary: '#d4af37',
        background: '#f8f6f0',
    },
    neutral: {
        label: 'Corporativo neutro',
        primary: '#334155',
        background: '#f8fafc',
    },
    custom: {
        label: 'Personalizado',
        primary: '#1e3a8a',
        background: '#f0f4f8',
    },
};

function slugFromName(name) {
    return String(name || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

const EMPTY_FORM = {
    name: '',
    slug: '',
    slugTouched: false,
    brandPreset: 'neutral',
    primaryColor: BRAND_PRESETS.neutral.primary,
    backgroundColor: BRAND_PRESETS.neutral.background,
    logoUrl: '',
    seedCidMaster: true,
};

export default function SuperadminTenants() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [createdTenant, setCreatedTenant] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteSlugInput, setDeleteSlugInput] = useState('');

    async function load() {
        setLoading(true);
        setError('');
        try {
            const res = await api.getSuperadminTenants();
            setRows(res.data || []);
        } catch (e) {
            setError(e.message || 'No se pudo cargar');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    const closeModal = useCallback(() => {
        if (busy) return;
        setModalOpen(false);
        setCreatedTenant(null);
        setForm(EMPTY_FORM);
    }, [busy]);

    function openCreateModal() {
        setError('');
        setCreatedTenant(null);
        setForm(EMPTY_FORM);
        setModalOpen(true);
    }

    function updateForm(patch) {
        setForm((prev) => {
            const next = { ...prev, ...patch };
            if (patch.name != null && !prev.slugTouched) {
                next.slug = slugFromName(patch.name);
            }
            if (patch.brandPreset != null && patch.brandPreset !== 'custom') {
                const p = BRAND_PRESETS[patch.brandPreset];
                if (p) {
                    next.primaryColor = p.primary;
                    next.backgroundColor = p.background;
                }
            }
            return next;
        });
    }

    async function handleCreate(e) {
        e.preventDefault();
        if (!form.name.trim() || !form.slug.trim()) return;
        setBusy(true);
        setError('');
        try {
            const body = {
                name: form.name.trim(),
                slug: form.slug.trim(),
                brandPreset: form.brandPreset,
                seedCidMaster: form.seedCidMaster,
            };
            if (form.brandPreset === 'custom') {
                body.primaryColor = form.primaryColor;
                body.backgroundColor = form.backgroundColor;
            }
            if (form.logoUrl.trim()) {
                body.logoUrl = form.logoUrl.trim();
            }
            const res = await api.createSuperadminTenant(body);
            setCreatedTenant(res.data);
            await load();
        } catch (err) {
            setError(err.message || 'Error al crear');
        } finally {
            setBusy(false);
        }
    }

    async function toggleActive(id, next) {
        setBusy(true);
        try {
            await api.setSuperadminTenantActive(id, next);
            await load();
        } catch (e) {
            setError(e.message || 'Error al actualizar');
        } finally {
            setBusy(false);
        }
    }

    async function setSolutionProfile(id, solutionProfile) {
        setBusy(true);
        setError('');
        try {
            await api.setSuperadminSolutionProfile(id, solutionProfile);
            await load();
        } catch (e) {
            setError(e.message || 'Error al cambiar perfil');
        } finally {
            setBusy(false);
        }
    }

    function openDeleteModal(tenant) {
        setError('');
        setDeleteSlugInput('');
        setDeleteTarget(tenant);
    }

    function closeDeleteModal() {
        if (busy) return;
        setDeleteTarget(null);
        setDeleteSlugInput('');
    }

    async function handleDeleteConfirmed() {
        if (!deleteTarget) return;
        setBusy(true);
        setError('');
        try {
            await api.deleteSuperadminTenant(deleteTarget.id, deleteSlugInput.trim());
            closeDeleteModal();
            await load();
        } catch (e) {
            setError(e.message || 'No se pudo eliminar la empresa');
        } finally {
            setBusy(false);
        }
    }

    if (loading && rows.length === 0) {
        return (
            <div className="sat-loading">
                <div className="spinner" />
                <p className="text-muted">Cargando empresas…</p>
            </div>
        );
    }

    return (
        <div className="sat-page container container-wide animate-in">
            <header className="sat-hero">
                <div className="sat-hero-row">
                    <div>
                        <h1>Empresas en ALINEA</h1>
                        <p className="text-muted">
                            Modelo multi-tenant y marca blanca: cada cliente tiene datos, maestros CID y personal
                            aislados. Cree la empresa aquí y complete la activación con la plantilla maestra de
                            implementación.
                        </p>
                    </div>
                    <button type="button" className="btn btn-primary" onClick={openCreateModal}>
                        + Nueva empresa
                    </button>
                </div>
            </header>

            {error && !modalOpen && (
                <div className="glass-card sat-alert" role="alert">
                    {error}
                </div>
            )}

            <section className="sat-cards-section" aria-label="Empresas y enlaces de acceso">
                <h2 className="sat-h2">Clientes y enlaces de acceso</h2>
                <p className="text-muted text-sm sat-cards-intro">
                    Guarde o reenvíe el enlace de cada empresa si un usuario lo pierde. El personal debe entrar solo
                    por su URL (<code>/login/slug</code>), no por el acceso genérico de plataforma.
                </p>
                {rows.length === 0 ? (
                    <div className="glass-card sat-empty-card text-muted">
                        No hay empresas. Use «Nueva empresa» para crear la primera.
                    </div>
                ) : (
                    <div className="sat-tenant-cards">
                        {rows.map((t) => (
                            <article key={t.id} className="glass-card sat-tenant-card">
                                <div className="sat-tenant-card-head">
                                    <span
                                        className="sat-brand-swatch sat-brand-swatch-lg"
                                        style={{ background: t.primary_color || '#334155' }}
                                        title={t.primary_color || ''}
                                    />
                                    <div className="sat-tenant-card-title">
                                        <h3>{t.name}</h3>
                                        <code className="sat-tenant-slug">{t.slug}</code>
                                    </div>
                                    <span className={`sat-status${t.is_active ? '' : ' sat-status-off'}`}>
                                        {t.is_active ? 'Activa' : 'Desactivada'}
                                    </span>
                                </div>
                                <p className="text-sm text-muted sat-tenant-meta">
                                    {t.active_employees ?? 0} empleados activos
                                </p>
                                <label className="sat-profile-label text-sm">
                                    Perfil de solución
                                    <select
                                        className="input w-100"
                                        value={t.solution_profile || 'rotacion'}
                                        disabled={busy}
                                        onChange={(e) => void setSolutionProfile(t.id, e.target.value)}
                                    >
                                        <option value="rotacion">Rotación (v1)</option>
                                        <option value="estado_funcional">Estado funcional</option>
                                    </select>
                                </label>
                                <TenantLoginLink
                                    slug={t.slug}
                                    name={t.name}
                                    loginUrl={t.login_url}
                                />
                                <div className="sat-tenant-card-actions">
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        disabled={busy}
                                        onClick={() => toggleActive(t.id, t.is_active ? 0 : 1)}
                                    >
                                        {t.is_active ? 'Desactivar' : 'Activar'}
                                    </button>
                                    <Link className="btn btn-secondary btn-sm" to="/admin/finance">
                                        Finanzas
                                    </Link>
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        disabled={busy}
                                        onClick={() => openDeleteModal(t)}
                                    >
                                        Eliminar
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </section>

            <p className="sat-foot-hint text-muted text-sm">
                Para cargar áreas, empleados, maestros CID y mensajes en bloque, use{' '}
                <Link to="/admin/onboarding">Implementación (plantilla maestra)</Link>.
            </p>

            {deleteTarget &&
                createPortal(
                    <div className="sat-modal-backdrop" role="presentation" onClick={closeDeleteModal}>
                        <div
                            className="glass-card sat-modal sat-delete-modal"
                            role="alertdialog"
                            aria-modal="true"
                            onClick={(ev) => ev.stopPropagation()}
                        >
                            <h2>Eliminar empresa</h2>
                            <p className="text-muted">
                                Se borrarán todos los datos de <strong>{deleteTarget.name}</strong> (personal,
                                encuestas, alertas, CID). No se puede deshacer.
                            </p>
                            <p className="sat-delete-hint text-sm">
                                Escriba el slug <code>{deleteTarget.slug}</code> para confirmar:
                            </p>
                            <input
                                className="input w-100"
                                value={deleteSlugInput}
                                onChange={(e) => setDeleteSlugInput(e.target.value)}
                                placeholder={deleteTarget.slug}
                                autoComplete="off"
                                disabled={busy}
                            />
                            <div className="sat-modal-actions">
                                <button type="button" className="btn btn-secondary" disabled={busy} onClick={closeDeleteModal}>
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    disabled={
                                        busy ||
                                        deleteSlugInput.trim().toLowerCase() !==
                                            String(deleteTarget.slug).toLowerCase()
                                    }
                                    onClick={() => void handleDeleteConfirmed()}
                                >
                                    {busy ? 'Eliminando…' : 'Eliminar definitivamente'}
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

            {modalOpen &&
                createPortal(
                    <div
                        className="sat-modal-backdrop"
                        role="presentation"
                        onClick={closeModal}
                    >
                        <div
                            className="glass-card sat-modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="sat-modal-title"
                            onClick={(ev) => ev.stopPropagation()}
                        >
                            {createdTenant ? (
                                <div className="sat-modal-success">
                                    <h2 id="sat-modal-title">Empresa creada</h2>
                                    <p className="text-muted">
                                        <strong>{createdTenant.name}</strong> (<code>{createdTenant.slug}</code>) está
                                        lista como tenant independiente.
                                    </p>
                                    <div className="sat-created-login">
                                        <TenantLoginLink
                                            slug={createdTenant.slug}
                                            name={createdTenant.name}
                                            loginUrl={createdTenant.login_url}
                                        />
                                    </div>
                                    <ul className="sat-success-steps">
                                        <li>Área base «General» creada.</li>
                                        <li>
                                            Comparta el enlace de arriba con RRHH; quedará también en la tarjeta de{' '}
                                            <strong>{createdTenant.name}</strong> en este listado.
                                        </li>
                                        {form.seedCidMaster ? (
                                            <li>Maestro CID inicial aplicado (operativo, técnico, supervisión).</li>
                                        ) : (
                                            <li>
                                                Sin maestro CID: configúrelo en Calculadora CID o en la plantilla
                                                Excel.
                                            </li>
                                        )}
                                        <li>
                                            Siguiente paso recomendado: subir la plantilla maestra con personal,
                                            áreas y datos completos.
                                        </li>
                                    </ul>
                                    <div className="sat-modal-actions">
                                        <Link
                                            to="/admin/onboarding"
                                            className="btn btn-primary"
                                            onClick={closeModal}
                                        >
                                            Ir a implementación
                                        </Link>
                                        <Link
                                            to="/calculator"
                                            className="btn btn-secondary"
                                            onClick={closeModal}
                                        >
                                            Ajustar CID
                                        </Link>
                                        <button type="button" className="btn btn-secondary" onClick={closeModal}>
                                            Cerrar
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="sat-modal-head">
                                        <div>
                                            <h2 id="sat-modal-title">Nueva empresa (marca blanca)</h2>
                                            <p className="sat-modal-sub text-muted">
                                                Define identidad y colores del cliente. Los usuarios verán esta marca al
                                                iniciar sesión.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            className="sat-modal-close"
                                            aria-label="Cerrar"
                                            onClick={closeModal}
                                            disabled={busy}
                                        >
                                            ×
                                        </button>
                                    </div>

                                    {error && (
                                        <div className="sat-modal-alert" role="alert">
                                            {error}
                                        </div>
                                    )}

                                    <form className="sat-modal-form" onSubmit={handleCreate}>
                                        <fieldset className="sat-fieldset">
                                            <legend>Identidad</legend>
                                            <div className="sat-field">
                                                <label className="sat-label" htmlFor="sat-name">
                                                    Nombre visible
                                                </label>
                                                <input
                                                    id="sat-name"
                                                    className="input w-100"
                                                    value={form.name}
                                                    onChange={(e) => updateForm({ name: e.target.value })}
                                                    placeholder="Ej. Aceros del Norte"
                                                    required
                                                />
                                            </div>
                                            <div className="sat-field">
                                                <label className="sat-label" htmlFor="sat-slug">
                                                    Slug (identificador único)
                                                </label>
                                                <input
                                                    id="sat-slug"
                                                    className="input w-100"
                                                    value={form.slug}
                                                    onChange={(e) =>
                                                        updateForm({
                                                            slug: e.target.value
                                                                .toLowerCase()
                                                                .replace(/[^a-z0-9-]/g, ''),
                                                            slugTouched: true,
                                                        })
                                                    }
                                                    placeholder="ej. aceros-norte"
                                                    required
                                                />
                                                <span className="sat-field-hint">
                                                    Solo letras minúsculas, números y guiones. Se genera desde el nombre.
                                                </span>
                                            </div>
                                        </fieldset>

                                        <fieldset className="sat-fieldset">
                                            <legend>Marca blanca</legend>
                                            <div className="sat-preset-grid">
                                                {Object.entries(BRAND_PRESETS).map(([key, p]) => (
                                                    <label
                                                        key={key}
                                                        className={`sat-preset-card${form.brandPreset === key ? ' sat-preset-card-on' : ''}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="brandPreset"
                                                            value={key}
                                                            checked={form.brandPreset === key}
                                                            onChange={() => updateForm({ brandPreset: key })}
                                                        />
                                                        <span
                                                            className="sat-preset-swatch"
                                                            style={{
                                                                background:
                                                                    key === 'custom'
                                                                        ? form.primaryColor
                                                                        : p.primary,
                                                            }}
                                                        />
                                                        <span className="sat-preset-label">{p.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                            {form.brandPreset === 'custom' && (
                                                <div className="sat-color-row">
                                                    <div className="sat-field">
                                                        <label className="sat-label" htmlFor="sat-primary">
                                                            Color primario
                                                        </label>
                                                        <div className="sat-color-input">
                                                            <input
                                                                type="color"
                                                                id="sat-primary"
                                                                value={form.primaryColor}
                                                                onChange={(e) =>
                                                                    updateForm({ primaryColor: e.target.value })
                                                                }
                                                            />
                                                            <input
                                                                className="input"
                                                                value={form.primaryColor}
                                                                onChange={(e) =>
                                                                    updateForm({ primaryColor: e.target.value })
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="sat-field">
                                                        <label className="sat-label" htmlFor="sat-bg">
                                                            Color de fondo
                                                        </label>
                                                        <div className="sat-color-input">
                                                            <input
                                                                type="color"
                                                                id="sat-bg"
                                                                value={form.backgroundColor}
                                                                onChange={(e) =>
                                                                    updateForm({ backgroundColor: e.target.value })
                                                                }
                                                            />
                                                            <input
                                                                className="input"
                                                                value={form.backgroundColor}
                                                                onChange={(e) =>
                                                                    updateForm({ backgroundColor: e.target.value })
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="sat-field">
                                                <label className="sat-label" htmlFor="sat-logo">
                                                    URL del logo (opcional)
                                                </label>
                                                <input
                                                    id="sat-logo"
                                                    className="input w-100"
                                                    type="url"
                                                    value={form.logoUrl}
                                                    onChange={(e) => updateForm({ logoUrl: e.target.value })}
                                                    placeholder="https://…"
                                                />
                                            </div>
                                            <div
                                                className="sat-brand-preview"
                                                style={{
                                                    '--preview-primary': form.primaryColor,
                                                    '--preview-bg': form.backgroundColor,
                                                }}
                                            >
                                                <span className="sat-brand-preview-badge">Vista previa</span>
                                                <strong style={{ color: 'var(--preview-primary)' }}>
                                                    {form.name.trim() || 'Nombre de empresa'}
                                                </strong>
                                                <span className="text-muted text-sm">ALINEA · marca blanca</span>
                                            </div>
                                        </fieldset>

                                        <fieldset className="sat-fieldset">
                                            <legend>Maestro inicial</legend>
                                            <label className="sat-check">
                                                <input
                                                    type="checkbox"
                                                    checked={form.seedCidMaster}
                                                    onChange={(e) =>
                                                        updateForm({ seedCidMaster: e.target.checked })
                                                    }
                                                />
                                                <span>
                                                    Aplicar maestro CID demo en el área «General» (reclutamiento,
                                                    indemnización, brecha y maestría por nivel).
                                                </span>
                                            </label>
                                            <p className="sat-field-hint">
                                                Para una implementación completa (personal, más áreas, causales y
                                                mensajes), use después la{' '}
                                                <Link to="/admin/onboarding" onClick={(e) => e.stopPropagation()}>
                                                    plantilla maestra Excel
                                                </Link>
                                                .
                                            </p>
                                        </fieldset>

                                        <div className="sat-modal-actions sat-modal-actions-sticky">
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={closeModal}
                                                disabled={busy}
                                            >
                                                Cancelar
                                            </button>
                                            <button type="submit" className="btn btn-primary" disabled={busy}>
                                                {busy ? 'Creando…' : 'Crear empresa'}
                                            </button>
                                        </div>
                                    </form>
                                </>
                            )}
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}
